/**
 * ⭐⭐⭐ SEEDING THE ANALYZER FROM A STATED NOI. Joseph's ruling, 2026-08-31.
 *
 * WHAT THIS FIXES. Two OMs went through the front door on 2026-08-31 and both were read with
 * nothing changed. The Murrieta ATD centre is stabilised and 100% occupied; its extraction carries
 * `noi: 195703` and `cap_rate: 5.62` exactly, its property header printed 5.62% - **and the
 * analyzer six inches below printed cap 0.00% and Year 1 NOI $0.** The analyzer builds NOI from
 * `gross_income`, which that OM never states (the extractor's own confidence for the field is 0),
 * so it started from nothing and ignored the NOI directly above it. Victorville failed the same
 * way in the other direction: expenses ran against zero income, NOI went negative, and a negative
 * NOI over a cap rate priced a negative exit.
 *
 * ⛔ WHY IT IS NOT A ONE-LINE FALLBACK. `pgi = statedNoi` is wrong the moment the app applies its
 * own vacancy factor or its automatic expense defaults on top: a figure that is ALREADY NET would
 * be netted a second time. Joseph's own rulebook states the governing principle - **revenue and
 * expenses must always be on the same basis** - so this seeds BOTH sides together, or not at all.
 *
 * ⛔ IT REFUSES FAR MORE OFTEN THAN IT ACTS, exactly like `rebaseToInPlace`. A rent roll outranks
 * it, a stated gross income outranks it, and a missing or negative NOI stops it.
 */
export interface StatedNoiSeedInput {
  /** The NOI the document states, as extracted. */
  statedNoi: number | null | undefined;
  /**
   * ⭐⭐ THE RATE THE SCHEDULE ENGINE ACTUALLY RESOLVED, not a boolean and not a row count.
   *
   * 🚨 This was `hasRentRoll: boolean` for about an hour, computed at the call site as
   * `tenants.length > 0`, and the browser found it immediately: Murrieta's extraction carries
   * exactly ONE tenant row with `lease_end: null`, `leased_sf: null` and no rent at all - the OM
   * says "Tenant-level rent amount not disclosed". A row count called that a rent roll, the seed
   * refused, and the screen still read cap 0.00%. **A tenant row is not a rent roll; a PRICEABLE
   * rent is.** Passing the resolved rate makes the wrong question impossible to ask.
   */
  rentRollRate: number | null | undefined;
  /** A gross income the document states, if any. */
  statedGrossIncome: number | null | undefined;
  /** Landlord operating expenses the document states, if any. */
  statedExpenses: number | null | undefined;
}

export interface StatedNoiSeed {
  /** What to seed as potential gross income. */
  potentialGrossIncome: number;
  /** Always 0 - a stated NOI is already net of actual occupancy. */
  vacancyRate: number;
  /** True when the expense lines must be zeroed to keep both sides on one basis. */
  zeroExpenses: boolean;
  /** What was done and why, for the screen. A silent seed is a guess. */
  because: string;
}

/**
 * Returns the income basis that makes the analyzer recompute the stated NOI exactly,
 * or `null` when it should not act.
 */
export function seedFromStatedNoi(input: StatedNoiSeedInput): StatedNoiSeed | null {
  const { statedNoi, rentRollRate, statedGrossIncome, statedExpenses } = input;

  // ⛔ Nothing to seed from.
  if (typeof statedNoi !== 'number' || !Number.isFinite(statedNoi) || statedNoi <= 0) return null;
  // ⛔ The leases outrank a single summary figure - they carry dates, steps and expiries.
  // ⛔ But only when they actually PRICE: a tenant row with no rent resolves to no rate.
  if (typeof rentRollRate === 'number' && rentRollRate > 0) return null;
  // ⛔ A stated gross income IS the income basis; deriving one would model the deal twice.
  if (typeof statedGrossIncome === 'number' && statedGrossIncome > 0) return null;

  const expenses = typeof statedExpenses === 'number' && Number.isFinite(statedExpenses)
    ? statedExpenses
    : 0;

  if (expenses > 0) {
    // The document gives both sides. Gross the NOI back up so the app's own subtraction
    // returns it, and KEEP the stated expenses - they are evidence, and discarding evidence
    // is the defect that shipped twice in two days in August.
    return {
      potentialGrossIncome: statedNoi + expenses,
      vacancyRate: 0,
      zeroExpenses: false,
      because: `Income derived from the stated NOI: the document gives no rent roll and no gross income, `
        + `so the NOI was grossed back up by the operating expenses it does state.`,
    };
  }

  // The document gives a net figure and nothing to net it against, which is how an
  // absolute-net OM markets a deal. Take the NOI as net and hold the expense lines at zero
  // so the two sides stay on one basis.
  return {
    potentialGrossIncome: statedNoi,
    vacancyRate: 0,
    zeroExpenses: true,
    because: `Income seeded from the stated NOI: the document gives no rent roll and no gross income, `
      + `so the NOI is taken as net and the landlord expense lines are held at zero to keep both `
      + `sides on one basis.`,
  };
}
